import { useEffect, useMemo, useRef, useState } from "react"
import ChartPanel from "../../charts/ChartPanel"
import { formatNumber } from "../../charts/formatters"
import { formatShortDateInTimeZone } from "../../../lib/utils/timezone"
import HorizontalScrollCards from "./HorizontalScrollCards"
import "./TopStories.css"

const SORT_COLUMNS = [
  { key: "impressions", label: "IMPRESSIONS" },
  { key: "reach", label: "REACH" },
  { key: "replies", label: "REPLIES" },
  { key: "retentionRate", label: "RETENTION RATE" },
]

const STORY_PERFORMANCE_LIMIT = 10

const STORY_SEGMENT_COLORS = ["#5320A4", "#6B2FC4", "#8745D8", "#A66FE8", "#C9A8F0", "#E4D0FA"]

const STORY_PERFORMANCE_GROUPS = [
  {
    value: "top",
    label: "Top 10",
    description: "Highest-performing stories",
  },
  {
    value: "bottom",
    label: "Bottom 10",
    description: "Lowest-performing stories",
  },
]

export const TOP_STORY_PLATFORMS = ["instagram"]

const PLATFORM_LABELS = {
  instagram: "Instagram",
}

const getPlatformLabel = (platformId) => PLATFORM_LABELS[platformId] || platformId

const formatShortDate = (value) => formatShortDateInTimeZone(value, "UTC")

const formatCompactImpressions = (value) => {
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`
  return String(value)
}

const PRESET_RANGE_LABELS = {
  last_30: "Last 30 days",
  month_to_date: "This month",
  previous_month: "Last month",
}

const buildRangeSuffix = (dateRangePreset, dateRangeLabel) => {
  const presetLabel = PRESET_RANGE_LABELS[dateRangePreset]
  if (presetLabel) return presetLabel
  if (dateRangeLabel) return dateRangeLabel
  return "selected range"
}

const buildSubtitle = (availablePlatforms, dateRangePreset, dateRangeLabel) => {
  const suffix = ` - ${buildRangeSuffix(dateRangePreset, dateRangeLabel)}`
  if (availablePlatforms.length === 0) {
    return `Connect Instagram to view story performance${suffix}`
  }
  return `Instagram stories ranked by performance${suffix}`
}

const SortArrow = ({ direction }) => (
  <svg
    className="topStoriesSortArrow"
    width="10"
    height="10"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    {direction === "desc" ? (
      <path d="M6 2v8M3.5 7.5L6 10l2.5-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M6 10V2M3.5 4.5L6 2l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
)

const TopStories = ({
  data = [],
  availablePlatforms = [],
  dateRangePreset,
  dateRangeLabel,
  footerActions,
  loading = false,
  onVisibleStoriesChange,
}) => {
  const [sortBy, setSortBy] = useState("impressions")
  const [sortDir, setSortDir] = useState("desc")
  const [rankMode, setRankMode] = useState("top")
  const [rankModeOpen, setRankModeOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(STORY_PERFORMANCE_LIMIT)
  const [platform, setPlatform] = useState("instagram")
  const [platformFilterOpen, setPlatformFilterOpen] = useState(false)
  const platformFilterRef = useRef(null)
  const rankModeRef = useRef(null)

  const platformOptions = useMemo(
    () => availablePlatforms.filter((platformId) => platformId === "instagram"),
    [availablePlatforms],
  )

  const showPlatformToggle = platformOptions.length > 0

  useEffect(() => {
    if (platform !== "instagram") {
      setPlatform("instagram")
    }
  }, [platform])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (platformFilterRef.current && !platformFilterRef.current.contains(event.target)) {
        setPlatformFilterOpen(false)
      }
      if (rankModeRef.current && !rankModeRef.current.contains(event.target)) {
        setRankModeOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (!rankModeOpen && !platformFilterOpen) return
    const closeMenus = () => {
      setRankModeOpen(false)
      setPlatformFilterOpen(false)
    }
    window.addEventListener("scroll", closeMenus, true)
    return () => window.removeEventListener("scroll", closeMenus, true)
  }, [rankModeOpen, platformFilterOpen])

  const platformStories = useMemo(() => {
    if (availablePlatforms.length === 0) return []
    return data.filter((story) => story.platform === "instagram")
  }, [availablePlatforms, data])

  const rankedStories = useMemo(() => {
    const next = [...platformStories]
    next.sort((a, b) => {
      const aVal = a[sortBy] ?? 0
      const bVal = b[sortBy] ?? 0
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      const ranked = rankMode === "top" ? -cmp : cmp
      return sortDir === "desc" ? ranked : -ranked
    })
    return next
  }, [platformStories, sortBy, rankMode, sortDir])

  const visibleStories = useMemo(
    () => rankedStories.slice(0, visibleCount),
    [rankedStories, visibleCount],
  )

  useEffect(() => {
    setVisibleCount(STORY_PERFORMANCE_LIMIT)
  }, [sortBy, sortDir, rankMode, platformStories])

  const showLoadMore = rankedStories.length > visibleStories.length

  useEffect(() => {
    onVisibleStoriesChange?.(visibleStories)
  }, [visibleStories, onVisibleStoriesChange])

  const subtitle = buildSubtitle(availablePlatforms, dateRangePreset, dateRangeLabel)

  const handleSortClick = (column) => {
    if (sortBy === column) {
      setSortDir((dir) => (dir === "desc" ? "asc" : "desc"))
    } else {
      setSortBy(column)
      setSortDir("desc")
    }
  }

  return (
    <ChartPanel
      dataChartId="topStories"
      className="topStoriesSection"
      title="Top Story"
      subtitle={subtitle}
      actions={
        showPlatformToggle ? (
          <div className="topPostsHeaderActions">
            <div className="topPostsControls">
              <div className="platformFilterWrapper">
                <div className="platformFilterTabs">
                  {platformOptions.map((platformId) => (
                    <button
                      key={platformId}
                      type="button"
                      className={`platformFilterTab ${platform === platformId ? "platformFilterTab--active" : ""}`}
                      onClick={() => setPlatform(platformId)}
                    >
                      {getPlatformLabel(platformId)}
                    </button>
                  ))}
                </div>
                <div ref={platformFilterRef} className="dashboardFilterSelect platformFilterDropdown">
                  <button
                    className="dashboardFilterSelectButton"
                    type="button"
                    onClick={() => setPlatformFilterOpen((prev) => !prev)}
                  >
                    <span className="dashboardFilterSelectLabel">Platform</span>
                    <span className="dashboardFilterSelectValue">{getPlatformLabel(platform)}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {platformFilterOpen && (
                    <div className="dashboardFilterSelectMenu">
                      {platformOptions.map((platformId) => (
                        <button
                          key={platformId}
                          type="button"
                          className={`dashboardFilterSelectOption ${platform === platformId ? "dashboardFilterSelectOption--active" : ""}`}
                          onClick={() => {
                            setPlatform(platformId)
                            setPlatformFilterOpen(false)
                          }}
                        >
                          {getPlatformLabel(platformId)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null
      }
      footerActions={footerActions}
      loading={loading}
    >
      <div className="topPostsWrapper topStoriesWrapper">
        {availablePlatforms.length > 0 && (
          <div className="topPostsSortHeader">
            <div className="topPostsSortHeader__left">
              <span className="topPostsSortLabel">Sort by</span>
              {SORT_COLUMNS.map((column) => (
                <button
                  key={column.key}
                  type="button"
                  className={`topPostsSortCol ${sortBy === column.key ? "topPostsSortCol--active" : ""}`}
                  onClick={() => handleSortClick(column.key)}
                >
                  {column.label}
                  {sortBy === column.key ? <SortArrow direction={sortDir} /> : null}
                </button>
              ))}
            </div>
            <div className="postPerformanceRankSummary">
              <span className="postPerformanceRankSummaryLabel">Showing</span>
              <div ref={rankModeRef} className="dashboardFilterSelect postPerformanceGroupSelect">
                <button
                  type="button"
                  className="dashboardFilterSelectButton postPerformanceGroupButton"
                  aria-haspopup="listbox"
                  aria-expanded={rankModeOpen}
                  aria-label={`Performance group: ${rankMode === "top" ? "Top 10" : "Bottom 10"}`}
                  onClick={() => setRankModeOpen((prev) => !prev)}
                >
                  <span className="dashboardFilterSelectValue">
                    {rankMode === "top" ? "Top 10" : "Bottom 10"}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {rankModeOpen && (
                  <div className="dashboardFilterSelectMenu postPerformanceGroupMenu" role="listbox" aria-label="Story performance group">
                    {STORY_PERFORMANCE_GROUPS.map((group) => (
                      <button
                        key={group.value}
                        type="button"
                        role="option"
                        aria-selected={rankMode === group.value}
                        className={`dashboardFilterSelectOption postPerformanceGroupOption ${rankMode === group.value ? "dashboardFilterSelectOption--active" : ""}`}
                        onClick={() => {
                          setRankMode(group.value)
                          setRankModeOpen(false)
                        }}
                      >
                        <span className="postPerformanceGroupOptionLabel">{group.label}</span>
                        <span className="postPerformanceGroupOptionDescription">{group.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="postPerformanceRankSummaryTotal">
                of {platformStories.length} {platformStories.length === 1 ? "story" : "stories"}
              </span>
            </div>
          </div>
        )}

        <HorizontalScrollCards className="topPostsCards topPostsCards--scroll topStoriesCards" showNavArrows>
          {availablePlatforms.length === 0 && (
            <div className="topStoriesEmptyState">
              No story data available. Connect Instagram to see story performance here.
            </div>
          )}
          {availablePlatforms.length > 0 && rankedStories.length === 0 && (
            <div className="topStoriesEmptyState">
              No story data available for the selected range.
            </div>
          )}
          {visibleStories.map((story) => {
            const perStory = Array.isArray(story.impressionsPerStory) ? story.impressionsPerStory : []
            const seriesTotal = perStory.reduce((sum, value) => sum + value, 0)
            return (
              <div key={story.storyIndex} className="topPostCard topStoryCard">
                <div className={`topStoryThumbnail ${story.storyThumbnails?.[0] ? "topStoryThumbnail--hasImage" : ""}`}>
                  {story.storyThumbnails?.[0] && (
                    <img src={story.storyThumbnails[0]} alt="" loading="lazy" />
                  )}
                  <div className="topStoryThumbnailPlaceholder" />
                  <span className="topStoryCountBadge">
                    {story.storyCount} {story.storyCount === 1 ? "story" : "stories"}
                  </span>
                </div>

                <div className="topPostContent">
                  <div className="topPostMetaRow">
                    <span className="topPostDate">{formatShortDate(story.date)}</span>
                  </div>

                  <div className="topPostEngagementSection">
                    <div className="topPostEngagementLabel">Retention rate</div>
                    <div className="topPostEngagementValue">{story.retentionRate}%</div>
                  </div>

                  <div className="topPostMetrics">
                    <div className="topPostMetric">
                      <span className="topPostMetricLabel">Stories</span>
                      <span className="topPostMetricValue dashboard-tabular-nums">{story.storyCount}</span>
                    </div>
                    <div className="topPostMetric">
                      <span className="topPostMetricLabel">Impressions</span>
                      <span className="topPostMetricValue dashboard-tabular-nums">
                        {formatNumber(story.impressions)}
                      </span>
                    </div>
                    <div className="topPostMetric">
                      <span className="topPostMetricLabel">Reach</span>
                      <span className="topPostMetricValue dashboard-tabular-nums">
                        {formatNumber(story.reach)}
                      </span>
                    </div>
                    <div className="topPostMetric">
                      <span className="topPostMetricLabel">Replies</span>
                      <span className="topPostMetricValue dashboard-tabular-nums">
                        {formatNumber(story.replies)}
                      </span>
                    </div>
                  </div>

                  <div className="topStoryImpressionsSection">
                    <div className="topStoryImpressionsLabel">Impressions per story</div>
                    <div
                      className="topStoryImpressionsBar"
                      role="img"
                      aria-label={`Impressions per story: ${perStory
                        .map((value, index) => `story ${index + 1} ${formatNumber(value)}`)
                        .join(", ")}`}
                    >
                      {perStory.map((value, frameIndex) => (
                        <span
                          key={`${story.storyIndex}-segment-${frameIndex}`}
                          className="topStoryImpressionsBarSegment"
                          style={{
                            flexGrow: value,
                            flexBasis: 0,
                            backgroundColor:
                              story.segmentColors?.[frameIndex] || STORY_SEGMENT_COLORS[frameIndex],
                          }}
                          title={`Story ${frameIndex + 1}: ${formatNumber(value)} impressions`}
                        />
                      ))}
                    </div>
                    <div className="topStoryImpressionsValues">
                      {perStory.map((value, frameIndex) => (
                        <span
                          key={`${story.storyIndex}-value-${frameIndex}`}
                          className="topStoryImpressionsValue dashboard-tabular-nums"
                          title={`Story ${frameIndex + 1}: ${formatNumber(value)} impressions`}
                        >
                          {formatCompactImpressions(value)}
                        </span>
                      ))}
                    </div>
                    <div className="topStoryImpressionsTotal dashboard-tabular-nums">
                      {formatNumber(seriesTotal)} total
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
          {showLoadMore && (
            <button
              type="button"
              className="topPostLoadMoreTile"
              onClick={() =>
                setVisibleCount((count) =>
                  Math.min(count + STORY_PERFORMANCE_LIMIT, rankedStories.length),
                )
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Load 10 more
            </button>
          )}
        </HorizontalScrollCards>
      </div>
    </ChartPanel>
  )
}

export default TopStories
